// Utility functions for URL handling, particularly for widget embedding behavior

export type WidgetConfig = {
    productPathPattern?: string; // e.g. "/products/${sku}"
};

// Detect if the app is running embedded (e.g., in an iframe)
export function isEmbedded(): boolean {
    try {
        // Allow a debug override so local tests (non-iframe) can behave as embedded
        const globalAny = window as any;
        if (
            globalAny?.PublicWidgetConfig?.forceEmbedded === true ||
            globalAny?.FORCE_WIDGET_EMBEDDED === true ||
            // Treat widget mode as embedded even if not in an iframe
            globalAny?.PublicWidgetConfig?.widgetMode === true
        ) {
            return true;
        }
        return window.self !== window.top;
    } catch {
        return true;
    }
}

// Read widget config from a global object set by the embedding script
// Supported locations:
// - (window as any).PublicWidgetConfig.productPathPattern
// - (window as any).PUBLIC_WIDGET_PRODUCT_PATH (string)
export function getWidgetConfig(): Required<WidgetConfig> {
    const globalAny = window as any;
    const patternFromObject = globalAny?.PublicWidgetConfig?.productPathPattern as string | undefined;
    const patternFromLegacy = globalAny?.PUBLIC_WIDGET_PRODUCT_PATH as string | undefined;

    const productPathPattern = patternFromObject ?? patternFromLegacy ?? "/products/${sku}";
    return { productPathPattern };
}

// Build a product-relative path from SKU using the configured pattern
export function buildProductPathFromSku(sku: string): string {
    const { productPathPattern } = getWidgetConfig();
    return productPathPattern.replace("${sku}", encodeURIComponent(sku));
}

// --- Host page product-page detection ---
// The widget runs inside the host page's window (not a cross-origin iframe),
// so window.location is always the live host URL, including after SPA navigation.

// Returns the current host page URL. Always current when called.
export function getHostUrl(): string {
    try {
        return window.location.href;
    } catch {
        return "";
    }
}

// Extracts a product SKU from a URL whose path follows the `.../product(s)/<sku>`
// convention, on any host domain. Returns null if the path is not a product page.
// Examples:
//   /product/12345          -> "12345"
//   /products/1725359       -> "1725359"
//   /en/product/12345-name  -> "12345-name"
//   /catalog/products/abc/  -> "abc"
// A public.gr / public.cy SKU as it appears as the last segment of a product URL (e.g. "2072768", "0834676").
const PUBLIC_SKU_SEGMENT = /^\d{4,12}$/;

export function extractProductSkuFromUrl(url?: string): string | null {
    const target = url ?? getHostUrl();
    if (!target) return null;
    try {
        const u = new URL(target, getHostUrl() || undefined);
        const segments = u.pathname.split("/").filter(Boolean);
        // Accept both "product" and "products" path segments.
        const idx = Math.max(segments.lastIndexOf("product"), segments.lastIndexOf("products"));
        if (idx === -1 || idx === segments.length - 1) {
            console.debug("[url] extractProductSkuFromUrl: not a product page", { url: target, pathname: u.pathname, segments });
            return null;
        }
        // public.gr / public.cy product pages end in the numeric SKU:
        //   /product/<category>/<subcategory>/<slug>/<sku>
        // so a numeric last segment wins; short host paths (/product/<sku>) keep the segment after "product".
        const after = segments.slice(idx + 1).map(segment => decodeURIComponent(segment));
        const last = after[after.length - 1];
        const sku = (PUBLIC_SKU_SEGMENT.test(last) ? last : after[0]) || null;
        console.debug("[url] extractProductSkuFromUrl: detected SKU", { url: target, sku });
        return sku;
    } catch (e) {
        console.debug("[url] extractProductSkuFromUrl: failed to parse URL", { url: target, error: e });
        return null;
    }
}

// SKU of the product the host page is currently showing, or null if not on a
// product page. Reads the live URL each call — call it any time you need it.
export function getCurrentProductSku(): string | null {
    // Explicit host-provided SKU wins (native embedding sets this via ChatWidget.init /
    // ChatWidget.setHostContext). Falls back to the browser URL for the web integration.
    try {
        const explicit = (window as any)?.PublicWidgetConfig?.hostContext?.productSku;
        if (typeof explicit === "string" && explicit.trim()) {
            return explicit.trim();
        }
    } catch {}
    return extractProductSkuFromUrl();
}

// True when the host page is currently on a product page (`.../product/<sku>`).
export function isOnProductPage(): boolean {
    return getCurrentProductSku() !== null;
}

// Try to extract a SKU from a public.gr or public.cy product URL
export function extractSkuFromPublicUrl(url: string): string | null {
    if (!url) return null;
    try {
        const u = new URL(url, window.location.origin);
        const host = u.hostname.toLowerCase();
        if (host !== "www.public.gr" && host !== "www.public.cy") return null;
        // Only accept paths under /product/
        if (!u.pathname.startsWith("/product/")) return null;
        const segments = u.pathname.split("/").filter(Boolean);
        const last = segments[segments.length - 1] || "";
        return last || null;
    } catch {
        return null;
    }
}

// Compute href for a product link, rewriting to relative when embedded
export function toProductHref(inputUrl?: string, skuMaybe?: string): string | undefined {
    if (!inputUrl && !skuMaybe) return inputUrl;
    if (!isEmbedded()) return inputUrl;
    const { productPathPattern } = getWidgetConfig();
    if (productPathPattern === "") return inputUrl;
    const sku = skuMaybe || (inputUrl ? extractSkuFromPublicUrl(inputUrl) : null);
    if (!sku) return inputUrl;
    return buildProductPathFromSku(sku);
}

// Decide target attribute based on embedding; break out of iframe when embedded
export function linkTarget(): "_top" | "_blank" | undefined {
    if (!isEmbedded()) return "_blank";
    const { productPathPattern } = getWidgetConfig();
    if (productPathPattern === "") return "_blank";
    return "_top";
}
